import {
  Box,
  Typography,
  Stack,
  Chip,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from '@mui/material';
import {
  CloseRounded,
  ReceiptLongRounded,
  CheckCircleOutlineRounded,
  RadioButtonUncheckedRounded,
  HourglassEmptyRounded,
  WorkspacePremiumRounded,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { type FinancialRow } from '../../../../hooks/use-reports';

import { fmt } from './shared';
import { installmentChip } from './plan-detail.model';

export interface PlanDetailDialogProps {
  plan: FinancialRow | null;
  onClose: () => void;
  isDark: boolean;
}

export function PlanDetailDialog({ plan, onClose, isDark }: PlanDetailDialogProps) {
  const { t } = useTranslation('sis');

  const STATUS_ICON = {
    paid: <CheckCircleOutlineRounded sx={{ color: 'success.main', fontSize: 18 }} />,
    overdue: <HourglassEmptyRounded sx={{ color: 'warning.main', fontSize: 18 }} />,
    partial: <RadioButtonUncheckedRounded sx={{ color: 'info.main', fontSize: 18 }} />,
    pending: <RadioButtonUncheckedRounded sx={{ color: 'text.disabled', fontSize: 18 }} />,
  };
  const STATUS_COLOR: Record<string, 'success' | 'warning' | 'info' | 'default'> = {
    paid: 'success',
    overdue: 'warning',
    partial: 'info',
    pending: 'default',
  };

  const sorted = plan
    ? [...plan.installments].sort((a, b) => a.dueDate.localeCompare(b.dueDate))
    : [];

  // "Pagado" counts money received; the cuota checkmarks count money imputed to
  // cuotas. When those disagree the table silently stops adding up to the total
  // — a duplicate receipt, or a payment nobody imputed. Say so instead.
  const overAllocated = sorted.filter((i) => i.allocatedAmount > i.amount);
  const unreconciled = plan ? plan.paidAmount - plan.allocatedAmount : 0;
  // The domain lets each payment's allocations miss its amount by up to 1 unit,
  // so a gap that small is rounding, not a mismatch. Anything above it isn't.
  const beyondRounding = plan ? Math.abs(unreconciled) > plan.verifiedPaymentCount : false;
  const reconciliationWarning = plan
    ? overAllocated.length > 0
      ? t('reports.planDetail.overAllocatedWarning', {
          installments: overAllocated.map((i) => i.label).join(', '),
        })
      : beyondRounding
        ? t(
            unreconciled > 0
              ? 'reports.planDetail.unreconciledWarning'
              : 'reports.planDetail.overImputedWarning',
            { amount: fmt(Math.abs(unreconciled), plan.currency) },
          )
        : null
    : null;

  return (
    <Dialog
      open={plan !== null}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: isDark ? 'rgba(15,15,25,0.97)' : 'background.paper',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box sx={{ color: 'primary.main', display: 'flex' }}>
            <ReceiptLongRounded />
          </Box>
          <Box>
            <Typography fontWeight={800} variant="h6">
              {plan?.studentName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {plan?.planType} · {fmt(plan?.totalAmount ?? 0, plan?.currency)}
            </Typography>
          </Box>
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      {plan?.isExempt && (
        <Box px={3} pt={1}>
          <Chip
            icon={<WorkspacePremiumRounded />}
            size="small"
            label={t('reports.planDetail.exempt', {
              reason: plan.exemptionReason ?? t('reports.planDetail.exemptDefaultReason'),
            })}
            color="warning"
            variant="filled"
            sx={{ fontSize: 12, height: 26, maxWidth: '100%' }}
          />
        </Box>
      )}

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Summary bar */}
        <Stack
          direction="row"
          gap={3}
          px={3}
          py={1.5}
          sx={{
            bgcolor: isDark ? 'rgba(99,102,241,0.07)' : 'rgba(99,102,241,0.04)',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('reports.planDetail.total')}
            </Typography>
            <Typography fontWeight={700}>{fmt(plan?.totalAmount ?? 0, plan?.currency)}</Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('reports.planDetail.paid')}
            </Typography>
            <Typography fontWeight={700} color="success.main">
              {fmt(plan?.paidAmount ?? 0, plan?.currency)}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('reports.planDetail.pending')}
            </Typography>
            <Typography fontWeight={700} color="primary.main">
              {fmt(plan?.pendingAmount ?? 0, plan?.currency)}
            </Typography>
          </Box>
          {(plan?.overdueAmount ?? 0) > 0 && (
            <>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('reports.planDetail.overdue')}
                </Typography>
                <Typography fontWeight={700} color="warning.main">
                  {fmt(plan?.overdueAmount ?? 0, plan?.currency)}
                </Typography>
              </Box>
            </>
          )}
        </Stack>

        {reconciliationWarning && (
          <Box px={3} py={1.5}>
            <Alert severity="warning" sx={{ fontSize: 13 }}>
              {reconciliationWarning}
            </Alert>
          </Box>
        )}

        {/* Installment list */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    color: 'text.secondary',
                    fontWeight: 600,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  },
                }}
              >
                <TableCell sx={{ pl: 3 }}>{t('reports.planDetail.installment')}</TableCell>
                <TableCell>{t('reports.planDetail.dueDate')}</TableCell>
                <TableCell align="right">{t('reports.planDetail.amount')}</TableCell>
                <TableCell align="center" sx={{ pr: 3 }}>
                  {t('reports.planDetail.status')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((inst, idx) => {
                const chip = installmentChip(inst);
                const st = chip.status;
                return (
                  <TableRow
                    key={inst.id || idx}
                    sx={{
                      '&:last-child td': { borderBottom: 0 },
                      bgcolor:
                        st === 'overdue'
                          ? isDark
                            ? 'rgba(245,158,11,0.04)'
                            : 'rgba(245,158,11,0.03)'
                          : st === 'paid'
                            ? isDark
                              ? 'rgba(34,197,94,0.03)'
                              : 'rgba(34,197,94,0.02)'
                            : 'transparent',
                    }}
                  >
                    <TableCell sx={{ pl: 3 }}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        {STATUS_ICON[st]}
                        <Typography
                          fontSize={13}
                          fontWeight={inst.type === 'enrollment' ? 700 : 400}
                        >
                          {inst.label || inst.id}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {inst.dueDate || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={600} fontSize={13}>
                        {fmt(inst.amount, plan?.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ pr: 3 }}>
                      <Chip
                        size="small"
                        label={t(`reports.planDetail.${chip.key}`, {
                          paid: fmt(chip.paid, plan?.currency),
                          amount: fmt(chip.amount, plan?.currency),
                        })}
                        color={STATUS_COLOR[st] ?? 'default'}
                        variant="outlined"
                        sx={{ fontSize: 11, height: 22 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" size="small" onClick={onClose}>
          {t('reports.planDetail.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
